import { lazy as dynamic, Suspense, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio/react';

import {
  DrawerClose,
  DrawerContent,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
} from '@paid-ui/components/drawer';
import { LoadingIndicator } from '@paid-ui/components/loading-indicator';
import { StepFormContainer, StepFormContent, StepFormHeader } from '@paid-ui/components/step-form';

import { closeNestedFlyout } from './actions';
import { flyoutManager, NestedFlyoutType } from './model';

const MobileVerificationForm = dynamic(() => import('@paid-ui/forms/mobile-verification'));
const IdentityCheckIntro = dynamic(
  () => import('@paid-ui/forms/manage-payout-account/identity-check'),
);
const EditPersonalInfo = dynamic(() => import('@paid-ui/forms/edit-personal'));
const CompleteProfile = dynamic(() => import('@paid-ui/forms/complete-profile'));
const EditBusinessInfo = dynamic(() => import('@paid-ui/forms/edit-business'));
const EditTrusteesInfo = dynamic(() => import('@paid-ui/forms/edit-trustees'));
const EditIndividualTrustees = dynamic(() => import('@paid-ui/forms/edit-individual-trustees'));
const EditSignatoryForm = dynamic(() => import('@paid-ui/forms/edit-signatory'));
const SecurePaidDoc = dynamic(() => import('@paid-ui/blocks/securepaid-doc'));
const EvidenceMapView = dynamic(() => import('@paid-ui/blocks/evidence-map-view'));
const RetentionClaimDetails = dynamic(() => import('@paid-ui/blocks/RetentionClaimDetails'));
const RevokeContractForm = dynamic(() => import('@paid-ui/forms/revoke-contract'));
const DeleteDraftContract = dynamic(() => import('@paid-ui/blocks/delete-draft-contract'));

interface Props {
  children?: React.ReactNode;
  showHeader?: boolean;
  className?: string;
  hideCloseButton?: boolean;
  closeOnEscapeKeyDown?: boolean;
  closeOnClickOutside?: boolean;
}

const preventDefault = (event: Event) => event.preventDefault();

export const NestedFlyout: React.FC<Props> = ({
  children,
  hideCloseButton,
  showHeader,
  className,
  closeOnEscapeKeyDown = true,
  closeOnClickOutside = true,
}) => {
  const { nestedOpen, nestedType, nestedDetails } = useSnapshot(flyoutManager);

  const childrenComponent = useMemo(() => {
    switch (nestedType) {
      case NestedFlyoutType.VERIFY_MOBILE: {
        return <MobileVerificationForm />;
      }
      case NestedFlyoutType.IDENTITY_CHECK: {
        return <IdentityCheckIntro />;
      }
      case NestedFlyoutType.EDIT_PERSONAL: {
        return <EditPersonalInfo />;
      }
      case NestedFlyoutType.EDIT_BUSINESS: {
        return <EditBusinessInfo />;
      }
      case NestedFlyoutType.EDIT_TRUSTEE: {
        return <EditTrusteesInfo />;
      }
      case NestedFlyoutType.EDIT_INDIVIDUAL_TRUSTEE: {
        return <EditIndividualTrustees />;
      }
      case NestedFlyoutType.EDIT_SIGNATORY: {
        return <EditSignatoryForm />;
      }
      case NestedFlyoutType.COMPLETE_PROFILE: {
        return <CompleteProfile />;
      }
      case NestedFlyoutType.SECUREPAID_DOC: {
        return <SecurePaidDoc />;
      }
      case NestedFlyoutType.EVIDENCE_MAP_VIEW: {
        return <EvidenceMapView />;
      }
      case NestedFlyoutType.RETENTION_CLAIM_DETAILS: {
        const { claimInfo } = nestedDetails ?? {};
        return <RetentionClaimDetails claimInfo={claimInfo} />;
      }
      case NestedFlyoutType.REVOKE_CONTRACT: {
        const { contractId } = nestedDetails ?? {};
        return <RevokeContractForm contractId={contractId} />;
      }
      case NestedFlyoutType.DELETE_DRAFT_CONTRACT: {
        return <DeleteDraftContract />;
      }
      default: {
        return children;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nestedType, nestedDetails]);

  if (showHeader) {
    return (
      <DrawerRoot open={nestedOpen} onOpenChange={closeNestedFlyout}>
        <DrawerPortal>
          <DrawerOverlay className="z-[502]" />
          <DrawerContent
            side="right"
            className={twMerge('z-[600] w-[752px]', className)}
            onEscapeKeyDown={closeOnEscapeKeyDown ? undefined : preventDefault}
            onInteractOutside={closeOnClickOutside ? undefined : preventDefault}
            onPointerDownOutside={closeOnClickOutside ? undefined : preventDefault}
          >
            <StepFormContainer
              currentStep={0}
              render={({ isHeaderSticky, isFooterSticky }) => (
                <>
                  <StepFormHeader sticky={isHeaderSticky} simple />
                  <StepFormContent className="max-w-full items-stretch justify-center gap-y-6">
                    <Suspense fallback={<LoadingIndicator />}>{childrenComponent}</Suspense>
                  </StepFormContent>
                </>
              )}
            />
          </DrawerContent>
        </DrawerPortal>
      </DrawerRoot>
    );
  }

  return (
    <DrawerRoot open={nestedOpen} onOpenChange={closeNestedFlyout}>
      <DrawerPortal>
        <DrawerOverlay className="z-[502]" />
        <DrawerContent
          side="right"
          className={twMerge('z-[600] w-[752px]', className)}
          onEscapeKeyDown={closeOnEscapeKeyDown ? undefined : preventDefault}
          onInteractOutside={closeOnClickOutside ? undefined : preventDefault}
          onPointerDownOutside={closeOnClickOutside ? undefined : preventDefault}
        >
          {childrenComponent}
          <DrawerClose hidden={hideCloseButton} />
        </DrawerContent>
      </DrawerPortal>
    </DrawerRoot>
  );
};
